import lineItemEnum from 'enums/lineItem.enum';
import orderEnum from 'enums/order.enum';
import processingStateEnum from 'enums/processingState.enum';

/**
 * Bulk Update — the advanced filters and the value comparisons they need.
 *
 * Filters never hit the server: the drug's orders are fetched once and narrowed in the
 * browser, so the counts, the facets and "Select all" all speak for the same set rather than
 * for one page of it. These comparisons are what "matches" means, so they live in one place.
 */

export const EMPTY_FILTERS = {
  facility: '',
  partner: '',
  ndc: '',
  qty: '',
  orderId: '',
  from: null,
  to: null,
};

export const isoDay = (value) => new Date(value).toISOString().slice(0, 10);

/* The partner filter offers names from getPartners while the rows carry names
 * from listBulkLabels. Those are two independent sources, so compare them
 * loosely -- an extra space or a case difference would otherwise silently
 * return no orders. A partner id on the row would be the real fix. */
export const samePartner = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/* NDC punctuation is not consistent between sources -- the bulk-labels filter
 * itself compares on hyphen-stripped values -- and review mode lets the user
 * type part of an NDC, so match on digits alone and accept a fragment. */
export const ndcDigits = (value) => String(value || '').replace(/\D/g, '');

/* The one NDC every listed order already carries, or '' when they differ, none is set, or there
 * are no orders. Setting the New NDC to this writes nothing -- core reports every such row as
 * alreadyApplied -- so the screen would stage a change, strike the current value through and
 * report "N orders updated" for work that never happened. Derived from the rows rather than from
 * the NDC filter so it also covers a set narrowed by partner, facility or date, and a drug whose
 * open orders all sit on one NDC with no filter at all. */
export const soleCurrentNdc = (rows) => {
  const distinct = new Set(rows.map((row) => ndcDigits(row.ndc)));
  const [only] = [...distinct];

  return distinct.size === 1 && only ? only : '';
};

/**
 * The selected rows that are actually on screen.
 *
 * The selection outlives the filters -- select all, then narrow to one quantity, and every row
 * selected before the filter is still selected behind it. Applying a value over the raw selection
 * therefore wrote it onto rows the operator could not see, and clearing the filter revealed them
 * all staged. The commit was never wrong (stagedIds derives from the visible rows) but the next
 * one would have been, because by then those rows are visible.
 *
 * Deliberately not solved by pruning the selection whenever a filter moves: a filter is used to
 * FIND rows, and dropping the selection each time would make an incremental selection across
 * several filters impossible.
 */
export const selectedAndVisible = (params) => {
  const { selectedIds, visibleRows } = params;
  const visible = new Set(visibleRows.map((row) => row.lineItemId));

  return selectedIds.filter((lineItemId) => visible.has(lineItemId));
};

/** The partners these rows carry, de-duplicated and sorted. Sibling of buildQtyFacet. */
export const buildPartnerFacet = (rows) =>
  [...new Set(rows.map((row) => row.partner).filter(Boolean))].sort((left, right) =>
    String(left).localeCompare(String(right), undefined, { sensitivity: 'base' })
  );

/* Moved out of useBulkOverride (2026-09-09): that file sits on the repo's max-lines limit and
 * this is a pure function of the operation, with no state of its own. */
const REVIEWABLE_ORDER_STATUSES = [
  orderEnum.OPEN,
  orderEnum.ORDER_VERIFICATION,
  orderEnum.ORDER_RE_VERIFICATION,
];
/* Takes the mode flags rather than the operation: importing bulkOverrideOperation would pull
 * api/requests -- and with it the export-to-csv ESM graph -- into this file's test, which has no
 * transform for it. The util does not need to know the enum to answer the question. */
export const standingFilterFor = ({
  isReview,
  isUnfulfillment,
  isFillLaterSplit,
  isFillLaterMove,
}) => {
  if (isReview) {
    return {
      taggedForReview: false,
      itemProcessingState: processingStateEnum.PROCESSING,
      orderStatuses: REVIEWABLE_ORDER_STATUSES,
    };
  }

  /* The mirror of the other modes: unfulfillment is the only operation that acts on an order
   * already filled, and only while it is still inside the window an order can be returned from. */
  if (isUnfulfillment) {
    return {
      lineItemStatus: lineItemEnum.PRODUCT_FULFILLED,
      orderStatuses: REVIEWABLE_ORDER_STATUSES,
    };
  }

  if (isFillLaterSplit || isFillLaterMove) {
    return {
      lineItemStatus: lineItemEnum.PRODUCT_FULFILLMENT,
      taggedForReview: false,
    };
  }

  return { lineItemStatus: lineItemEnum.PRODUCT_FULFILLMENT };
};
